import { Phone, Mail, MapPin, Building } from "lucide-react";
import { COMPANY } from "@/lib/site";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { EnquiryForm } from "@/components/EnquiryForm";

const CARDS = [
    {
        icon: Phone,
        title: "Telephone",
        testId: "contact-phone-card",
        content: (
            <a href={COMPANY.phoneHref} data-testid="contact-phone-link" className="font-display text-lg font-bold text-slate-900 transition-colors hover:text-emerald-700">
                {COMPANY.phoneDisplay}
            </a>
        ),
    },
    {
        icon: Mail,
        title: "Email",
        testId: "contact-email-card",
        content: (
            <a href={COMPANY.emailHref} data-testid="contact-email-link" className="break-all font-display text-lg font-bold text-slate-900 transition-colors hover:text-emerald-700">
                {COMPANY.email}
            </a>
        ),
    },
    {
        icon: MapPin,
        title: "Coverage",
        testId: "contact-coverage-card",
        content: <p className="font-display text-lg font-bold text-slate-900">England &amp; Wales</p>,
    },
    {
        icon: Building,
        title: "Registered Office",
        testId: "contact-office-card",
        content: (
            <address className="text-sm not-italic leading-relaxed text-slate-600">
                {COMPANY.registeredOffice.map((line) => (
                    <span key={line} className="block">{line}</span>
                ))}
            </address>
        ),
    },
];

export default function Contact() {
    return (
        <>
            <SEO
                title="Contact Us | Virtex Renewables"
                description="Contact Virtex Renewables about air source heat pumps for your property. Call 0333 014 0227 or email info@virtexrenewables.co.uk. Serving England & Wales."
            />

            <section className="relative overflow-hidden bg-slate-50 py-16 md:py-24" data-testid="contact-hero">
                <div className="pointer-events-none absolute -right-40 -top-40 h-[24rem] w-[24rem] rounded-full bg-emerald-100/70 blur-3xl" />
                <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal className="max-w-3xl">
                        <span className="inline-block rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">
                            Contact
                        </span>
                        <h1 className="mt-6 font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
                            Contact Virtex Renewables
                        </h1>
                        <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
                            Whether you're considering replacing an existing heating system or simply want to
                            understand whether a heat pump could work for your property, get in touch with our team.
                        </p>
                    </Reveal>

                    <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                        {CARDS.map((card, i) => (
                            <Reveal key={card.title} delay={i * 0.07}>
                                <div className="h-full rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid={card.testId}>
                                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50">
                                        <card.icon className="h-5 w-5 text-emerald-600" />
                                    </div>
                                    <p className="mt-4 text-xs font-bold uppercase tracking-[0.18em] text-slate-500">{card.title}</p>
                                    <div className="mt-2">{card.content}</div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section id="enquiry-form" className="scroll-mt-24 bg-white py-16 md:py-24" data-testid="contact-form-section">
                <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[1fr_1.25fr] lg:items-start lg:px-8">
                    <Reveal>
                        <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                            Request Your Free Heat Pump Assessment
                        </h2>
                        <p className="mt-4 text-base leading-relaxed text-slate-600">
                            Tell us a little about your property and our team can discuss your heating requirements
                            and the next steps.
                        </p>
                        <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-6">
                            <p className="text-sm font-semibold text-slate-900">Prefer to talk it through?</p>
                            <p className="mt-2 text-sm leading-relaxed text-slate-600">
                                Call{" "}
                                <a href={COMPANY.phoneHref} className="font-bold text-emerald-700 hover:underline" data-testid="contact-form-phone-link">
                                    {COMPANY.phoneDisplay}
                                </a>{" "}
                                or email{" "}
                                <a href={COMPANY.emailHref} className="font-bold text-emerald-700 hover:underline" data-testid="contact-form-email-link">
                                    {COMPANY.email}
                                </a>
                            </p>
                        </div>
                    </Reveal>
                    <Reveal delay={0.12}>
                        <EnquiryForm testIdPrefix="contact-enquiry" />
                    </Reveal>
                </div>
            </section>
        </>
    );
}
